import * as v from "valibot";

import { createConversationId } from "@textclub/common/random-id";
import type { PurchaseData, SubscriptionData } from "@textclub/common/types";

import { instrumentSdkMethod } from "../instrumentation";
import { parseArgs } from "../schema-utils";
import type { ModuleContext } from "./module-context";

const Duration = {
  MINUTE: 60 * 1000,
  SECOND: 1000,
} as const;

const BEGIN_PURCHASE_TIMEOUT = 5 * Duration.MINUTE;
const BEGIN_SUBSCRIPTION_TIMEOUT = 5 * Duration.MINUTE;
const COMPLETE_PURCHASE_TIMEOUT = 10 * Duration.SECOND;
const GET_INCOMPLETE_PURCHASES_TIMEOUT = 10 * Duration.SECOND;
const GET_PRODUCTS_TIMEOUT = 10 * Duration.SECOND;
const GET_SUBSCRIPTIONS_TIMEOUT = 10 * Duration.SECOND;

type GetSubscriptionsResult = {
  subscriptions: SubscriptionData[];
  signed: string;
};

const BeginPurchaseOpts = v.object({
  productSku: v.string(),
});

const CompletePurchaseOpts = v.object({
  purchaseToken: v.pipe(v.string(), v.nonEmpty()),
});

const BeginSubscriptionOpts = v.object({
  subscriptionSku: v.string(),
});

const CancelSubscriptionOpts = v.object({
  subscriptionSku: v.string(),
});

const ClaimRetentionOfferOpts = v.object({
  subscriptionSku: v.string(),
});

const CANCEL_SUBSCRIPTION_TIMEOUT = 5 * Duration.MINUTE;

/**
 * The PaymentsModule manages all payment operations.
 */
export class PaymentsModule {
  constructor(private ctx: ModuleContext) {
    this.ctx.registerMessageHandlers({
      BeginPlatformPurchaseResponse: (message) =>
        this.ctx.resolveWaiting(message),
      CompletePlatformPurchaseResponse: (message) =>
        this.ctx.resolveWaiting(message),
      GetIncompletePurchasesResponse: (message) =>
        this.ctx.resolveWaiting(message),
      GetGameProductsResponse: (message) => this.ctx.resolveWaiting(message),
      BeginPlatformSubscriptionResponse: (message) =>
        this.ctx.resolveWaiting(message),
      CancelPlatformSubscriptionResponse: (message) =>
        this.ctx.resolveWaiting(message),
      ClaimRetentionOfferResponse: (message) =>
        this.ctx.resolveWaiting(message),
      GetSubscriptionsResponse: (message) => this.ctx.resolveWaiting(message),
    });
  }

  @instrumentSdkMethod({ name: "payments.beginPurchase" })
  async beginPurchase(options: { productSku: string }): Promise<
    | { result: "success"; purchase: PurchaseData; purchaseSigned: string }
    | { result: "cancel" }
    | {
        result: "error";
        error: "internal_error" | "invalid_product";
      }
  > {
    parseArgs(BeginPurchaseOpts, options);

    this.ctx.assertInitialized();

    const conversationId = `beginPurchase_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    this.ctx.sendMessage({
      conversationId,
      type: "BeginPlatformPurchase",
      productSku: options.productSku,
    });

    const response = await this.ctx.waitForNextMessage(
      "BeginPlatformPurchaseResponse",
      conversationId,
      BEGIN_PURCHASE_TIMEOUT,
    );

    if (response.result === "error") {
      return { result: "error" as const, error: response.error };
    }

    if (response.result === "cancel") {
      return { result: "cancel" as const };
    }

    return {
      result: "success" as const,
      purchase: response.purchase,
      purchaseSigned: response.purchaseSigned,
    };
  }

  @instrumentSdkMethod({ name: "payments.getIncompletePurchases" })
  async getIncompletePurchases(): Promise<{
    hasMore: boolean;
    purchases: Array<PurchaseData>;
    purchasesSigned: string;
  }> {
    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      conversationId,
      type: "GetIncompletePurchases",
    });

    const response = await this.ctx.waitForNextMessage(
      "GetIncompletePurchasesResponse",
      conversationId,
      GET_INCOMPLETE_PURCHASES_TIMEOUT,
    );

    if (!response.success) {
      throw new Error("Failed to fetch incomplete purchases");
    }

    return {
      hasMore: response.hasMore,
      purchases: response.purchases,
      purchasesSigned: response.purchasesSigned,
    };
  }

  @instrumentSdkMethod({ name: "payments.completePurchase" })
  async completePurchase(options: { purchaseToken: string }): Promise<
    | { result: "success" }
    | {
        result: "error";
        error: "internal_error" | "invalid_token";
      }
  > {
    parseArgs(CompletePurchaseOpts, options);

    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      conversationId,
      type: "CompletePlatformPurchase",
      purchaseToken: options.purchaseToken,
    });

    const response = await this.ctx.waitForNextMessage(
      "CompletePlatformPurchaseResponse",
      conversationId,
      COMPLETE_PURCHASE_TIMEOUT,
    );

    if (response.result === "error") {
      return { result: "error" as const, error: response.error };
    }

    return { result: "success" as const };
  }

  @instrumentSdkMethod({ name: "payments.getProducts" })
  async getProducts(): Promise<
    Array<{
      sku: string;
      name: string;
      description: string | null;
      price: number;
      currency: string;
    }>
  > {
    this.ctx.assertInitialized();

    const conversationId = `getProducts_${Date.now()}_${Math.random().toString(36).substring(2)}`;

    this.ctx.sendMessage({
      type: "GetGameProducts",
      conversationId,
    });

    const response = await this.ctx.waitForNextMessage(
      "GetGameProductsResponse",
      conversationId,
      GET_PRODUCTS_TIMEOUT,
    );

    if (!response.success) {
      throw new Error("Failed to fetch products");
    }

    return response.products;
  }

  @instrumentSdkMethod({ name: "payments.beginSubscription" })
  async beginSubscription(options: { subscriptionSku: string }): Promise<
    | {
        result: "success";
        subscription: SubscriptionData;
        subscriptionSigned: string;
      }
    | { result: "cancel" }
    | {
        result: "error";
        error:
          | "internal_error"
          | "invalid_subscription"
          | "already_subscribed"
          | "guest_not_allowed";
      }
  > {
    parseArgs(BeginSubscriptionOpts, options);

    this.ctx.assertInitialized();

    const conversationId = `beginSubscription_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    this.ctx.sendMessage({
      conversationId,
      type: "BeginPlatformSubscription",
      subscriptionSku: options.subscriptionSku,
    });

    const response = await this.ctx.waitForNextMessage(
      "BeginPlatformSubscriptionResponse",
      conversationId,
      BEGIN_SUBSCRIPTION_TIMEOUT,
    );

    if (response.result === "error") {
      return { result: "error" as const, error: response.error };
    }

    if (response.result === "cancel") {
      return { result: "cancel" as const };
    }

    return {
      result: "success" as const,
      subscription: response.subscription,
      subscriptionSigned: response.subscriptionSigned,
    };
  }

  @instrumentSdkMethod({ name: "payments.getSubscriptions" })
  async getSubscriptions(): Promise<GetSubscriptionsResult> {
    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({ type: "GetSubscriptions", conversationId });

    const response = await this.ctx.waitForNextMessage(
      "GetSubscriptionsResponse",
      conversationId,
      GET_SUBSCRIPTIONS_TIMEOUT,
    );

    if (!response.success) {
      throw new Error("Failed to fetch subscriptions");
    }

    return {
      subscriptions: response.subscriptions,
      signed: response.signed,
    };
  }

  @instrumentSdkMethod({ name: "payments.cancelSubscription" })
  async cancelSubscription(options: { subscriptionSku: string }): Promise<
    | { result: "success" }
    | { result: "cancel" }
    | {
        result: "error";
        error:
          | "internal_error"
          | "not_found"
          | "not_active"
          | "guest_not_allowed";
      }
  > {
    parseArgs(CancelSubscriptionOpts, options);

    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      conversationId,
      type: "CancelPlatformSubscription",
      subscriptionSku: options.subscriptionSku,
    });

    const response = await this.ctx.waitForNextMessage(
      "CancelPlatformSubscriptionResponse",
      conversationId,
      CANCEL_SUBSCRIPTION_TIMEOUT,
    );

    if (response.result === "error") {
      return { result: "error" as const, error: response.error };
    }

    if (response.result === "cancel") {
      return { result: "cancel" as const };
    }

    return { result: "success" as const };
  }

  @instrumentSdkMethod({ name: "payments.claimRetentionOffer" })
  async claimRetentionOffer(options: { subscriptionSku: string }): Promise<
    | {
        result: "success";
        subscription: SubscriptionData;
        subscriptionSigned: string;
      }
    | {
        result: "error";
        error: "internal_error" | "not_eligible" | "guest_not_allowed";
      }
  > {
    parseArgs(ClaimRetentionOfferOpts, options);

    this.ctx.assertInitialized();

    const conversationId = createConversationId();
    this.ctx.sendMessage({
      conversationId,
      type: "ClaimRetentionOffer",
      subscriptionSku: options.subscriptionSku,
    });

    const response = await this.ctx.waitForNextMessage(
      "ClaimRetentionOfferResponse",
      conversationId,
      CANCEL_SUBSCRIPTION_TIMEOUT,
    );

    if (response.result === "error") {
      return { result: "error" as const, error: response.error };
    }

    return {
      result: "success" as const,
      subscription: response.subscription,
      subscriptionSigned: response.subscriptionSigned,
    };
  }

  /** Exposed for decorator use - delegates to context */
  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }
}
